import { useMemo, useState, useEffect } from 'react';
import { calculateScores, generateRecommendation, estimateFees } from '@/lib/scoring';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { TrendingUp, TrendingDown, Minus, Building, Wrench, ClipboardList, MapPin, FileText, Target, ArrowUp, ArrowDown, Info, Euro, Calculator, SlidersHorizontal, RotateCcw } from 'lucide-react';

const COLOR_MAP = {
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-800', border: 'border-emerald-200', accent: 'bg-emerald-600', badge: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  teal: { bg: 'bg-teal-50', text: 'text-teal-800', border: 'border-teal-200', accent: 'bg-teal-600', badge: 'bg-teal-100 text-teal-700 border-teal-200' },
  blue: { bg: 'bg-blue-50', text: 'text-blue-800', border: 'border-blue-200', accent: 'bg-blue-600', badge: 'bg-blue-100 text-blue-700 border-blue-200' },
  slate: { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', accent: 'bg-slate-600', badge: 'bg-slate-100 text-slate-600 border-slate-200' },
  yellow: { bg: 'bg-yellow-50', text: 'text-yellow-800', border: 'border-yellow-200', accent: 'bg-yellow-600', badge: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-200', accent: 'bg-amber-600', badge: 'bg-amber-100 text-amber-700 border-amber-200' },
  orange: { bg: 'bg-orange-50', text: 'text-orange-800', border: 'border-orange-200', accent: 'bg-orange-600', badge: 'bg-orange-100 text-orange-700 border-orange-200' },
  red: { bg: 'bg-red-50', text: 'text-red-800', border: 'border-red-200', accent: 'bg-red-600', badge: 'bg-red-100 text-red-700 border-red-200' },
  violet: { bg: 'bg-violet-50', text: 'text-violet-800', border: 'border-violet-200', accent: 'bg-violet-600', badge: 'bg-violet-100 text-violet-700 border-violet-200' },
};

const getColorClasses = (colorName) => COLOR_MAP[colorName] || COLOR_MAP.slate;

function ScoreBar({ label, value, icon: Icon }) {
  const color = value <= 30 ? 'bg-emerald-500' : value <= 60 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-2 text-muted-foreground">
          {Icon && <Icon className="h-4 w-4" />} {label}
        </span>
        <span className="font-bold tabular-nums">{value.toFixed(0)}</span>
      </div>
      <div className="h-2 w-full rounded-full bg-secondary overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${Math.min(value, 100)}%` }} />
      </div>
    </div>
  );
}

function getSizeCategory(units) {
  if (units <= 6) return 'Kleinstgemeinschaft';
  if (units <= 15) return 'Kleine WEG';
  if (units <= 30) return 'Mittlere WEG';
  if (units <= 60) return 'Große WEG';
  if (units <= 100) return 'Sehr große WEG';
  return 'Großanlage';
}

export default function ScorePanel({ data, settings, analysisId, propertyId, onDownloadPdf, onDownloadOfferPdf, isAdmin, onFeeOverride }) {
  // Always use JS calculation for live scoring (reacts to user input changes)
  const scores = useMemo(() => calculateScores(data, settings || {}), [data, settings]);
  const recommendation = useMemo(() => generateRecommendation(data, settings || {}), [data, settings]);
  const fees = useMemo(() => estimateFees(data, settings || {}), [data, settings]);

  const units = data.units || 1;
  const calculatedFee = recommendation.recommendedFee;
  const hasOverride = data.custom_fee_override != null && data.custom_fee_override !== calculatedFee;
  const recFee = data.custom_fee_override != null ? data.custom_fee_override : calculatedFee;

  // Slider range: generous range for full flexibility
  const sliderMin = Math.max(Math.floor(recommendation.recMin * 0.5), 5);
  const sliderMax = Math.ceil(recommendation.recMax * 2) + 10;
  const [sliderValue, setSliderValue] = useState(recFee);

  useEffect(() => {
    setSliderValue(data.custom_fee_override != null ? data.custom_fee_override : calculatedFee);
  }, [data.custom_fee_override, calculatedFee]);
  const monthlyTotal = Math.floor(recFee * units);
  const yearlyTotal = monthlyTotal * 12;
  const monthlyGross = Math.floor(monthlyTotal * 1.19);
  const yearlyGross = Math.floor(yearlyTotal * 1.19);

  const renovationCost = data.planned_renovations_cost || 0;
  const renovationFeePercent = settings?.renovation_fee_percent || 5;
  const renovationFee = renovationCost > 0 ? Math.round(renovationCost * (renovationFeePercent / 100) * 100) / 100 : 0;
  const renovationFeeMonthly = renovationFee > 0 ? Math.round((renovationFee / 12) * 100) / 100 : 0;

  // Use rating_color from scoring (matches backend's configurable rating_labels)
  const demandClr = COLOR_MAP[scores.ratingColor] || COLOR_MAP.slate;
  const demandColor = [demandClr.text, demandClr.bg, demandClr.border].join(' ');
  const DemandIcon = scores.demandPercentage <= 85 ? TrendingDown
    : scores.demandPercentage <= 100 ? Minus : TrendingUp;

  const assessmentConfig = settings?.assessment_labels?.[recommendation.assessment];
  const colorName = (typeof assessmentConfig === 'object' ? assessmentConfig?.color : null) || (
    recommendation.assessment === 'besonderer_bedarf' ? 'red' :
    recommendation.assessment === 'erhoeht' ? 'amber' :
    recommendation.assessment === 'reduziert' ? 'emerald' : 'blue'
  );
  const assessmentLabel = (typeof assessmentConfig === 'object' ? assessmentConfig?.label : assessmentConfig) || (
    recommendation.assessment === 'besonderer_bedarf' ? 'Besonderer Bedarf' :
    recommendation.assessment === 'erhoeht' ? 'Erhöhter Aufwand' :
    recommendation.assessment === 'reduziert' ? 'Günstige Konditionen' : 'Standardvertrag'
  );
  const colorClasses = getColorClasses(colorName);

  return (
    <div className="rounded-xl border bg-card shadow-sm overflow-hidden" data-testid="score-panel">
      {/* Header */}
      <div className="px-5 py-4 border-b bg-gradient-to-r from-slate-50 to-white">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold tracking-tight">Analyseergebnis</h3>
          {analysisId && (
            <Badge variant="outline" className="font-mono text-xs" data-testid="panel-analysis-id">
              {analysisId}
            </Badge>
          )}
        </div>
      </div>

      {/* === VERGÜTUNGSEMPFEHLUNG - Großer, professioneller Bereich === */}
      <div className="p-0" data-testid="recommendation-panel">
        {/* Assessment Badge Header */}
        <div className={`px-5 py-3 ${colorClasses.bg} border-b ${colorClasses.border} flex items-center gap-3`}>
          <div className={`p-2 rounded-lg ${colorClasses.accent}`}>
            <Target className="h-4 w-4 text-white" />
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Bewertung</p>
            <p className={`text-sm font-bold ${colorClasses.text}`} data-testid="assessment-label">{assessmentLabel}</p>
          </div>
          <Badge className={`ml-auto text-xs border ${colorClasses.badge}`} data-testid="size-category">
            {getSizeCategory(units)}
          </Badge>
        </div>

        {/* Main Recommendation Card */}
        <div className="p-5">
          <div className="bg-slate-900 rounded-xl p-6 text-white shadow-lg" data-testid="fee-recommendation-card">
            <p className="text-xs text-slate-400 uppercase tracking-widest mb-1 text-center">Vergütungsempfehlung</p>
            <div className="flex items-baseline justify-center gap-1.5 mb-1">
              <span className="text-5xl font-extrabold tabular-nums tracking-tight" data-testid="recommended-fee">
                {recFee.toFixed(0)}
              </span>
              <span className="text-lg text-slate-400 font-medium">€</span>
            </div>
            <p className="text-sm text-slate-400 text-center mb-5">pro Einheit / Monat (netto)</p>

            <Separator className="bg-slate-700 mb-5" />

            {/* Monthly & Yearly Summary */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-slate-800 rounded-lg p-3 text-center">
                <p className="text-[11px] text-slate-500 uppercase tracking-wider mb-1">Monatlich netto</p>
                <p className="text-xl font-bold tabular-nums" data-testid="recommended-total-monthly">{monthlyTotal} €</p>
              </div>
              <div className="bg-slate-800 rounded-lg p-3 text-center">
                <p className="text-[11px] text-slate-500 uppercase tracking-wider mb-1">Jährlich netto</p>
                <p className="text-xl font-bold tabular-nums" data-testid="recommended-total-yearly">{yearlyTotal} €</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <p className="text-[11px] text-slate-500">inkl. 19% MwSt./Monat</p>
                <p className="text-sm font-semibold tabular-nums text-slate-300" data-testid="monthly-gross">{monthlyGross} €</p>
              </div>
              <div className="text-center">
                <p className="text-[11px] text-slate-500">inkl. 19% MwSt./Jahr</p>
                <p className="text-sm font-semibold tabular-nums text-slate-300" data-testid="yearly-gross">{yearlyGross} €</p>
              </div>
            </div>

            {/* Renovation surcharge */}
            {renovationFee > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-700">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400 flex items-center gap-1.5">
                    <Wrench className="h-3.5 w-3.5" />
                    Modernisierungsvergütung ({renovationFeePercent}%)
                  </span>
                  <span className="font-semibold tabular-nums text-blue-400" data-testid="renovation-fee">+{renovationFee.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/Jahr</span>
                </div>
              </div>
            )}
          </div>

          {/* Admin Fee Override Slider */}
          {isAdmin && onFeeOverride && (
            <div className="mt-4 bg-slate-50 border rounded-xl p-4 space-y-3" data-testid="fee-override-section">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="h-4 w-4 text-slate-500" />
                  <span className="text-sm font-semibold text-slate-700">Preisanpassung</span>
                </div>
                {hasOverride && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs gap-1 text-muted-foreground hover:text-foreground"
                    onClick={() => { setSliderValue(calculatedFee); onFeeOverride(null); }}
                    data-testid="fee-override-reset"
                  >
                    <RotateCcw className="h-3 w-3" /> Zurücksetzen
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-4">
                <span className="text-xs text-muted-foreground tabular-nums w-8 text-right">{sliderMin}€</span>
                <Slider
                  value={[sliderValue]}
                  min={sliderMin}
                  max={sliderMax}
                  step={1}
                  onValueChange={([v]) => setSliderValue(v)}
                  onValueCommit={([v]) => onFeeOverride(v === calculatedFee ? null : v)}
                  className="flex-1"
                  data-testid="fee-override-slider"
                />
                <span className="text-xs text-muted-foreground tabular-nums w-8">{sliderMax}€</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold tabular-nums text-slate-900" data-testid="fee-override-value">{sliderValue} € / Einheit</span>
                {hasOverride && (
                  <span className="text-xs text-amber-600 font-medium">
                    {sliderValue > calculatedFee ? '+' : ''}{sliderValue - calculatedFee} € vs. Empfehlung
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Basis Info */}
          <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground px-1">
            <Info className="h-3.5 w-3.5 shrink-0" />
            <span>Basierend auf {units} Einheit{units !== 1 ? 'en' : ''} · Grundhonorar {Math.floor(fees.baseFeePerUnit)} €/Einheit</span>
          </div>

          {/* Mindesthonorar-Hinweis */}
          {recommendation.minimumFeeApplied && (
            <div className="mt-3 text-xs px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-700" data-testid="minimum-fee-notice">
              <div className="flex items-center gap-2">
                <Euro className="h-3.5 w-3.5 shrink-0" />
                <span className="font-medium">Mindesthonorar angewendet ({recommendation.minimumFeeEffective || recommendation.minimumFeeValue} €/Monat)</span>
              </div>
              {recommendation.minimumFeeReason && (
                <p className="mt-1 text-amber-600">
                  Anpassung aufgrund: {recommendation.minimumFeeReason}
                </p>
              )}
            </div>
          )}

          {/* Factors that influence the recommendation */}
          {recommendation.factors.length > 0 && (
            <div className="mt-4 space-y-1.5" data-testid="recommendation-factors">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1 mb-2">Einflussfaktoren</p>
              {recommendation.factors.map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded-md bg-slate-50 border">
                  {f.effect.includes('+') ? (
                    <ArrowUp className={`h-3.5 w-3.5 shrink-0 ${f.effect === '++' ? 'text-red-500' : 'text-amber-500'}`} />
                  ) : (
                    <ArrowDown className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
                  )}
                  <span className="text-slate-700 flex-1">{f.label}</span>
                  {f.detail && <span className="text-xs text-muted-foreground">{f.detail}</span>}
                </div>
              ))}
            </div>
          )}

          {/* Fee Range */}
          {recommendation.recMin !== recommendation.recMax && (
            <div className="mt-4 bg-slate-50 rounded-lg p-3 border" data-testid="fee-range">
              <p className="text-xs font-medium text-muted-foreground mb-2">Honorarspanne</p>
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold tabular-nums text-slate-600">{Math.floor(recommendation.recMin)} €</span>
                <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden relative">
                  <div className={`absolute h-full rounded-full ${colorClasses.accent}`}
                    style={{
                      left: `${Math.max(0, ((recommendation.recMin / recommendation.recMax) * 100) - 10)}%`,
                      right: '0%'
                    }}
                  />
                </div>
                <span className="text-sm font-semibold tabular-nums text-slate-600">{Math.floor(recommendation.recMax)} €</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bedarfsanzeige & Scores */}
      <div className="p-5 border-t space-y-5">
        <div className={`rounded-lg border p-4 text-center ${demandColor}`} data-testid="demand-badge">
          <DemandIcon className="h-5 w-5 mx-auto mb-1" />
          <div className="text-3xl font-bold tabular-nums" data-testid="demand-percentage">{scores.demandPercentage}%</div>
          <div className="text-xs font-medium mt-0.5 capitalize" data-testid="demand-category">{scores.demandCategory}</div>
        </div>

        <div className="space-y-4">
          <ScoreBar label="Gebäudekomplexität" value={scores.buildingComplexity} icon={Building} />
          <ScoreBar label="Gebäudezustand" value={scores.buildingCondition} icon={Wrench} />
          <ScoreBar label="Verwaltungshistorie" value={scores.historyScore} icon={ClipboardList} />
        </div>

        <div className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
          <span className="text-sm text-muted-foreground">Gesamtbewertung</span>
          <Badge variant="outline" className="font-bold tabular-nums" data-testid="total-score">
            {scores.totalScore.toFixed(1)} / 100
          </Badge>
        </div>
      </div>

      {/* Entfernungszuschlag */}
      {scores.distancePenalty > 0 && (
        <div className="mx-5 mb-5 flex items-center gap-2 text-sm text-amber-600 bg-amber-50 rounded-lg p-3 border border-amber-200">
          <MapPin className="h-4 w-4 shrink-0" />
          <span>Entfernungszuschlag: +{scores.distancePenalty.toFixed(1)} Punkte</span>
        </div>
      )}

      {/* PDF Download Buttons (Admin only) */}
      {isAdmin && propertyId && onDownloadPdf && (
        <div className="p-5 border-t space-y-2">
          <Button
            onClick={() => onDownloadPdf(propertyId)}
            variant="outline"
            className="w-full gap-2"
            data-testid="download-pdf-btn"
          >
            <FileText className="h-4 w-4" /> PDF-Report herunterladen
          </Button>
          {onDownloadOfferPdf && (
            <Button
              onClick={() => onDownloadOfferPdf(propertyId)}
              variant="outline"
              className="w-full gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="download-offer-pdf-btn"
            >
              <Euro className="h-4 w-4" /> Vorabangebot PDF
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
